import type {
  ResolvedTenantThemeDefinition,
  TenantBrandTheme,
  TenantThemeDefinition,
  TenantThemeRuntime,
} from "../core/types.ts";
import { PAD_THEME_FOUNDATION } from "../core/pad-foundation.ts";

function toGradient(theme: TenantBrandTheme): string {
  return `linear-gradient(135deg, ${theme.gradientStart} 0%, ${theme.gradientEnd} 100%)`;
}

function resolveBrandTheme(
  source:
    | TenantBrandTheme
    | TenantThemeDefinition
    | ResolvedTenantThemeDefinition
    | TenantThemeRuntime
): TenantBrandTheme {
  if ("brandTheme" in source) {
    return source.brandTheme;
  }

  if ("base" in source) {
    return source.base;
  }

  return source;
}

export function applyThemePresetToCSS(
  source:
    | TenantBrandTheme
    | TenantThemeDefinition
    | ResolvedTenantThemeDefinition
    | TenantThemeRuntime,
  root: HTMLElement = document.documentElement
): void {
  const theme = resolveBrandTheme(source);

  root.style.setProperty("--theme-primary", theme.primary);
  root.style.setProperty("--theme-primary-light", theme.secondary);
  root.style.setProperty("--theme-primary-dark", theme.gradientEnd);
  root.style.setProperty("--theme-secondary", theme.secondary);
  root.style.setProperty("--theme-tertiary", theme.tertiary);
  root.style.setProperty("--theme-accent", theme.accent ?? theme.primary);
  root.style.setProperty("--theme-gradient", toGradient(theme));
  root.style.setProperty("--theme-primary-gradient", toGradient(theme));
  root.style.setProperty("--theme-shadow-color", theme.shadowColor);
  root.style.setProperty(
    "--theme-tabs-active-bg",
    theme.tabsActive.background
  );
  root.style.setProperty(
    "--theme-tabs-active-border-color",
    theme.tabsActive.borderColor
  );
  root.style.setProperty(
    "--theme-tabs-active-color",
    theme.tabsActive.color
  );
  root.style.setProperty("--theme-tabs-active-shadow", theme.tabsActive.shadow);
  root.style.setProperty("--theme-font-family", theme.fontFamily);
  root.style.setProperty(
    "--theme-radius-sm",
    `${PAD_THEME_FOUNDATION.radii.sm}px`
  );
  root.style.setProperty(
    "--theme-radius-md",
    `${PAD_THEME_FOUNDATION.radii.md}px`
  );
  root.style.setProperty(
    "--theme-radius-lg",
    `${PAD_THEME_FOUNDATION.radii.lg}px`
  );
  root.style.setProperty(
    "--theme-radius-xl",
    `${PAD_THEME_FOUNDATION.radii.xl}px`
  );
  root.style.setProperty(
    "--theme-radius-full",
    `${PAD_THEME_FOUNDATION.radii.full}px`
  );
  root.style.setProperty("--theme-page-background", "#F7F9FB");
  root.style.setProperty("--theme-layout-bg", "#F7F9FB");
  root.style.setProperty("--theme-header-bg", "#ffffff");
  root.style.setProperty("--theme-sider-bg", "#ffffff");
  root.style.setProperty("--theme-text-primary", "#0f172a");
  root.style.setProperty("--theme-text-secondary", "#475569");
  root.style.setProperty("--theme-text-disabled", "rgba(15, 23, 42, 0.35)");
  root.style.setProperty("--theme-border-color", "#F7F9FB");
  root.style.setProperty(
    "--theme-shadow-small",
    PAD_THEME_FOUNDATION.shadow.small
  );
  root.style.setProperty(
    "--theme-shadow-medium",
    PAD_THEME_FOUNDATION.shadow.medium
  );
  root.style.setProperty(
    "--theme-shadow-large",
    PAD_THEME_FOUNDATION.shadow.large
  );
  root.style.setProperty("--theme-surface-subtle", theme.secondary);
  root.style.setProperty("--theme-menu-item-color", "#475569");
  root.style.setProperty("--theme-menu-icon-color", "#475569");
  root.style.setProperty("--theme-menu-font-weight", "500");
  root.style.setProperty("--theme-menu-font-size", "14px");
  root.style.setProperty("--theme-menu-line-height", "20px");
  root.style.setProperty("--theme-menu-icon-size", "20px");
  root.style.setProperty("--theme-menu-selected-bg", theme.secondary);
  root.style.setProperty("--theme-menu-selected-color", theme.primary);
  root.style.setProperty("--theme-menu-selected-icon-color", theme.primary);
  root.style.setProperty("--theme-menu-selected-font-weight", "600");
  root.style.setProperty("--theme-menu-hover-bg", theme.tertiary);
  root.style.setProperty("--theme-tooltip-bg", "#000000");
  root.style.setProperty("--theme-tooltip-text", "#ffffff");
}

export function removeThemeCSS(root: HTMLElement = document.documentElement): void {
  [
    "--theme-primary",
    "--theme-primary-light",
    "--theme-primary-dark",
    "--theme-secondary",
    "--theme-tertiary",
    "--theme-accent",
    "--theme-gradient",
    "--theme-primary-gradient",
    "--theme-shadow-color",
    "--theme-tabs-active-bg",
    "--theme-tabs-active-border-color",
    "--theme-tabs-active-color",
    "--theme-tabs-active-shadow",
    "--theme-font-family",
    "--theme-radius-sm",
    "--theme-radius-md",
    "--theme-radius-lg",
    "--theme-radius-xl",
    "--theme-radius-full",
    "--theme-page-background",
    "--theme-layout-bg",
    "--theme-header-bg",
    "--theme-sider-bg",
    "--theme-text-primary",
    "--theme-text-secondary",
    "--theme-text-disabled",
    "--theme-border-color",
    "--theme-shadow-small",
    "--theme-shadow-medium",
    "--theme-shadow-large",
    "--theme-surface-subtle",
    "--theme-menu-item-color",
    "--theme-menu-icon-color",
    "--theme-menu-font-weight",
    "--theme-menu-font-size",
    "--theme-menu-line-height",
    "--theme-menu-icon-size",
    "--theme-menu-selected-bg",
    "--theme-menu-selected-color",
    "--theme-menu-selected-icon-color",
    "--theme-menu-selected-font-weight",
    "--theme-menu-hover-bg",
    "--theme-tooltip-bg",
    "--theme-tooltip-text",
  ].forEach((property) => {
    root.style.removeProperty(property);
  });
}

export function getCSSVariable(
  variableName: string,
  root: HTMLElement = document.documentElement
): string {
  return getComputedStyle(root).getPropertyValue(`--${variableName}`).trim();
}
